import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { NotificationsOutlined, WorkOutline } from '@mui/icons-material';
import { updateNotificationStatus } from '../../../../actions/appNotifications';
import DashboardCard, { CardHeader } from '../DashboardCard/DashboardCard';
import { distanceDateFormat, sortArrayOfObjectsByDate } from '../../../../utils/helpers';
import { notificationStatuses } from '../../../../utils/constants';
import { useTranslation } from 'react-i18next';

function TnERow({ item, onClick }) {
  const { t } = useTranslation();
  const isDraft = item.Status === notificationStatuses(t).draft.apiStatus;
  const label   = isDraft
    ? t('dashboard.cards.newapprovercreated')
    : t('dashboard.cards.newapproveradded');

  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 px-5 py-3.5 border-b border-[#E2E8ED] last:border-b-0 bg-transparent border-none cursor-pointer hover:bg-[#F8FAFB] transition-colors text-left"
    >
      <div className="flex items-center justify-center w-6 h-6 rounded-full shrink-0 mt-0.5 bg-[#E6F4F8] text-[#0094B3]">
        <NotificationsOutlined sx={{ fontSize: 12 }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[14px] leading-snug m-0 ${item.Read ? 'text-[#6B7A85]' : 'text-[#022D41] font-medium'}`}>
          {label}
        </p>
        <span className="text-[12px] text-[#6B7A85] mt-0.5 block">
          {distanceDateFormat(item.CreatedDate)}
        </span>
      </div>
      {!item.Read && (
        <span className="w-2 h-2 rounded-full bg-[#0094B3] shrink-0 mt-2" />
      )}
    </button>
  );
}

function JobRow({ item, onClick }) {
  const { t } = useTranslation();
  const { JobAlert, MatchingCount, Read } = item;
  const location = JobAlert?.Location || JobAlert?.City || '';
  const label = `${JobAlert?.Keyword}: ${MatchingCount} ${MatchingCount > 1
    ? t('dashboard.cards.opportunities')
    : t('dashboard.cards.opportunity')}${location ? ` in ${location}` : ''}`;

  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 px-5 py-3.5 border-b border-[#E2E8ED] last:border-b-0 bg-transparent border-none cursor-pointer hover:bg-[#F8FAFB] transition-colors text-left"
    >
      <div className="flex items-center justify-center w-6 h-6 rounded-full shrink-0 mt-0.5 bg-[#D1FAE5] text-[#059669]">
        <WorkOutline sx={{ fontSize: 12 }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[14px] leading-snug m-0 ${Read ? 'text-[#6B7A85]' : 'text-[#022D41] font-medium'}`}>
          {label}
        </p>
        <span className="text-[12px] text-[#6B7A85] mt-0.5 block">
          {distanceDateFormat(item.CreatedDate)}
        </span>
      </div>
      {!Read && (
        <span className="w-2 h-2 rounded-full bg-[#059669] shrink-0 mt-2" />
      )}
    </button>
  );
}

export default function NotificationsCard({ animDelay = 0 }) {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();

  const tneList  = useSelector((s) => s.appNotifications?.notifications?.TnEnotification);
  const jobsList = useSelector((s) => s.appNotifications?.notifications?.JobMatchNotification);

  const sortedJobs = sortArrayOfObjectsByDate(jobsList ?? [], 'CreatedDate');
  const sortedTnE  = sortArrayOfObjectsByDate(tneList  ?? [], 'CreatedDate');
  const allItems   = [...sortedJobs, ...sortedTnE];
  const unreadCount = allItems.filter((n) => !n.Read).length;

  const handleTnE = (item) => {
    dispatch(updateNotificationStatus(item.Id, 'tne'));
    navigate('/dashboard/timesheets');
  };

  const handleJob = (item) => {
    const { JobAlert, Id } = item;
    dispatch(updateNotificationStatus(Id, 'JobAlert'));
    navigate(
      `/dashboard/Jobs?page=1&tab=publicjobs&subTab=saved&date=100` +
      `${JobAlert?.Keyword   ? `&searchTxt=${JobAlert.Keyword}`     : ''}` +
      `${JobAlert?.Location  ? `&location=${JobAlert.Location}`      : ''}` +
      `${JobAlert?.City      ? `&city=${JobAlert.City}`              : ''}` +
      `${JobAlert?.Country   ? `&country=${JobAlert.Country}`        : ''}`
    );
  };

  return (
    <DashboardCard noPadding animDelay={animDelay}>
      <CardHeader
        icon={<NotificationsOutlined />}
        title="Notifications"
        action={
          unreadCount > 0
            ? (
              <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#0094B3]">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#0094B3] text-white text-[10px] font-bold">
                  {unreadCount}
                </span>
                new
              </span>
            )
            : null
        }
      />

      {allItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2 text-[#6B7A85]">
          <NotificationsOutlined sx={{ fontSize: 30, opacity: 0.25 }} />
          <span className="text-[14px]">No new notifications</span>
        </div>
      ) : (
        <div className="flex flex-col">
          {sortedJobs.slice(0, 3).map((item) => (
            <JobRow key={item.Id} item={item} onClick={() => handleJob(item)} />
          ))}
          {sortedTnE.slice(0, 3).map((item) => (
            <TnERow key={item.Id} item={item} onClick={() => handleTnE(item)} />
          ))}
        </div>
      )}
    </DashboardCard>
  );
}